import { useMemo, useCallback } from 'react';
import get from 'lodash/get';
import { useSelector } from 'react-redux';

import { DOCUMENTATION_TYPES } from '../../../constants';
import { INSURANCE_FLOW_TYPE } from '../../../constants/insurance-flow-type';
import { INITIAL_INSURANCE_CARD_STATE } from '../../../constants/insurance/initial-insurance-card-state';

import {
  selectInsuranceLatestCheckData,
  selectProgressCareUserSkipInsurance,
} from '../../../redux/appointmentForm/selectors';
import { selectUserDocumentsList } from '../../../redux/profiles/selectors';
import { selectTargetUserSkipInsurance } from '../../../redux/context/selectors';
import { selectScheduledCompletedTasks } from '../../../redux/healthboard/selectors';
import { selectPaperworkUserSkipInsurance } from '../../../redux/paperwork/selectors/paperwork-get-user-details';

const useGetPatientMainInsuranceInfo = ({ flowType, toDoTaskData }) => {
  const latestInsuranceCheckedData = useSelector(
    selectInsuranceLatestCheckData,
  );

  const skipInsuranceProfileFlow = useSelector(selectTargetUserSkipInsurance);

  const scheduledCompletedTasks = useSelector(selectScheduledCompletedTasks);

  const userDocuments = useSelector(selectUserDocumentsList);

  const skipInsurancePaperworkFlow = useSelector(
    selectPaperworkUserSkipInsurance,
  );

  const skipInsuranceGetCareFlow = useSelector(
    selectProgressCareUserSkipInsurance,
  );

  const insuranceDataSource = useMemo(() => {
    if (flowType === INSURANCE_FLOW_TYPE.TO_DO) {
      const { itemId, isCompleted } = toDoTaskData || {};

      if (isCompleted) {
        const scheduledCompletedTask = scheduledCompletedTasks.find(
          task => task.id === itemId,
        );

        const patientInsurance = get(
          scheduledCompletedTask,
          'payload.patientInsurance',
          {},
        );

        return patientInsurance;
      }
    }

    return latestInsuranceCheckedData;
  }, [
    flowType,
    scheduledCompletedTasks,
    toDoTaskData,
    latestInsuranceCheckedData,
  ]);

  const skipInsurance = useMemo(() => {
    if (flowType === INSURANCE_FLOW_TYPE.TO_DO) {
      return get(insuranceDataSource, 'skipInsurance');
    }

    const map = new Map([
      [INSURANCE_FLOW_TYPE.PAPERWORK, skipInsurancePaperworkFlow],
      [INSURANCE_FLOW_TYPE.PROFILE, skipInsuranceProfileFlow],
      [INSURANCE_FLOW_TYPE.GET_CARE_FLOW, skipInsuranceGetCareFlow],
    ]);

    return map.get(flowType);
  }, [
    flowType,
    skipInsurancePaperworkFlow,
    skipInsuranceProfileFlow,
    insuranceDataSource,
    skipInsuranceGetCareFlow,
  ]);

  const isInsuranceToDoFlowType = flowType === INSURANCE_FLOW_TYPE.TO_DO;

  const {
    backSide,
    createdAt,
    createdDate,
    frontSide,
    groupNumber,
    id,
    isVerified,
    memberId,
    payer,
    primarySubscriber,
    userInputs,
  } = {
    ...insuranceDataSource,
  };

  const getInsuranceCard = useCallback(
    cardType => {
      const card = userDocuments.find(doc => doc.key === cardType);

      const attachment = get(card, 'attachment');

      return attachment || INITIAL_INSURANCE_CARD_STATE;
    },
    [userDocuments],
  );

  const frontSideValue = useMemo(() => {
    if (!isInsuranceToDoFlowType) {
      return getInsuranceCard(DOCUMENTATION_TYPES.CARD_1);
    }

    return frontSide;
  }, [getInsuranceCard, frontSide, isInsuranceToDoFlowType]);

  const backSideValue = useMemo(() => {
    if (!isInsuranceToDoFlowType) {
      return getInsuranceCard(DOCUMENTATION_TYPES.CARD_2);
    }

    return backSide;
  }, [getInsuranceCard, backSide, isInsuranceToDoFlowType]);

  const primarySubscriberValue = isInsuranceToDoFlowType
    ? primarySubscriber
    : get(userInputs, 'primarySubscriber', null);

  const groupNumberValue = isInsuranceToDoFlowType
    ? groupNumber
    : get(userInputs, 'groupNumber', '');

  const memberIdValue = isInsuranceToDoFlowType
    ? memberId
    : get(userInputs, 'memberId', '');

  const insuranceData = useMemo(
    () => ({
      backSide: backSideValue,
      createdAt,
      createdDate,
      frontSide: frontSideValue,
      groupNumber: groupNumberValue,
      id,
      isVerified,
      memberId: memberIdValue,
      payer,
      primarySubscriber: primarySubscriberValue,
      skipInsurance,
    }),
    [
      backSideValue,
      createdAt,
      createdDate,
      frontSideValue,
      groupNumberValue,
      id,
      isVerified,
      memberIdValue,
      payer,
      primarySubscriberValue,
      skipInsurance,
    ],
  );

  return insuranceData;
};

export default useGetPatientMainInsuranceInfo;



// WEBPACK FOOTER //
// ./src/components/InsurancesInfo/hooks/useGetPatientMainInsuranceInfo.js